import { useState, useMemo, useEffect, memo } from 'react';
import { getTableElementConfig, aiguilleJuste } from '../../utils/questionUtils';
import ChoixRenderer from './ChoixRenderer';
import NumberLineElementEleve from '../VueEvalNat/BilanEval/FormBilanEval/FormQuestion/Elements/NumberLineElementEleve';
import ProgrammeConstructionRenderer from './renderers/ProgrammeConstructionRenderer';
import HorlogeAiguillesRenderer from './renderers/HorlogeAiguillesRenderer';
import Repere2DRenderer from './renderers/Repere2DRenderer';
import { getChoixValue } from './interactifs/QCMEleve';

// ─── Utilitaires ───

// Grouper les répondants par réponse normalisée
const groupByAnswer = (repondants) => {
    const groups = new Map();
    repondants.forEach(r => {
        const key = r.reponse || '(vide)';
        if (!groups.has(key)) groups.set(key, { reponse: r.reponse, correcte: r.correcte, eleves: [] });
        groups.get(key).eleves.push(r.nom);
    });
    return [...groups.values()].sort((a, b) => {
        if (a.correcte !== b.correcte) return a.correcte ? -1 : 1;
        return b.eleves.length - a.eleves.length;
    });
};

// Parser les paires Relier depuis la réponse élève
const parsePaires = (reponse) => {
    try { const p = JSON.parse(reponse); return Array.isArray(p) ? p : []; }
    catch { return []; }
};

// Parser le classement depuis la réponse élève
const parseClassement = (reponse) => {
    try { const p = JSON.parse(reponse); return typeof p === 'object' && !Array.isArray(p) ? p : {}; }
    catch { return {}; }
};

// Parser une réponse au format [ligne|colonne][ligne|colonne]...
const parseTableReponse = (reponse) => {
    if (!reponse) return [];
    const re = /\[([^|]+)\|([^\]]+)\]/g;
    const pairs = [];
    let m;
    while ((m = re.exec(reponse)) !== null) pairs.push({ ligne: m[1], colonne: m[2] });
    return pairs;
};

// Extraire les mots des trous depuis une phrase {{mot}}
const extractTrous = (phrase) => {
    const matches = phrase?.match(/\{\{([^}]+)\}\}/g) || [];
    return matches.map(m => m.replace(/\{\{|\}\}/g, ''));
};

// ─── Sous-composants ───

// Couleurs pastels pour avatars — hash(nom) % 6
const AVATAR_PALETTE = [
    { bg: '#E6F1FB', text: '#0C447C' },
    { bg: '#EEEDFE', text: '#3C3489' },
    { bg: '#E1F5EE', text: '#085041' },
    { bg: '#FAECE7', text: '#712B13' },
    { bg: '#FBEAF0', text: '#72243E' },
    { bg: '#FAEEDA', text: '#633806' },
];
const getAvatarColor = (nom) => AVATAR_PALETTE[Math.abs([...nom].reduce((a, c) => a + c.charCodeAt(0), 0)) % AVATAR_PALETTE.length];
const capitalize = (s) => s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

// Compteur élèves — icône + nombre, discret
const ElevesCount = memo(({ count }) => {
    if (!count) return null;
    return (
        <span className="flex items-center gap-1 text-[11px] text-slate-400 flex-shrink-0">
            <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="8" cy="5" r="3" /><path d="M2 14c0-3 2.5-5 6-5s6 2 6 5" />
            </svg>
            {count}
        </span>
    );
});

// Chevron — signal "contenu derrière"
const Chevron = ({ open }) => (
    <span className={`text-slate-400 text-[10px] transition-transform duration-150 flex-shrink-0 ${open ? 'rotate-90' : ''}`}>›</span>
);

// Panneau expand — chips avec dot coloré, border-top fin
const ElevesPanel = memo(({ eleves }) => (
    <div className="flex flex-wrap gap-1.5 px-3 py-2 border-t" style={{ borderColor: 'rgba(226,232,240,0.5)', animation: 'fadeInUp 0.15s ease-out' }}>
        {eleves.map((nom, i) => {
            const color = getAvatarColor(nom);
            return (
                <span key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs text-slate-600">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color.text }} />
                    {capitalize(nom)}
                </span>
            );
        })}
    </div>
));

// ═══════════════════════════════════════
// ─── DistErreurs — composant réutilisable pour Relier, Classer, Ordonner ───
// Affiche : "Tout correct" + liste d'erreurs triées par fréquence
// ═══════════════════════════════════════
const DistErreurs = memo(({ label = 'connexion', correctEleves = [], total = 0, erreurs = [], expandAll = false }) => {
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    const correctPercent = total > 0 ? Math.round((correctEleves.length / total) * 100) : 0;

    return (
        <div className="space-y-2 rounded-xl">
            {/* Tout correct — fond blanc quand ouvert */}
            {(() => {
                const isCorrectOpen = expandedRows['correct'] !== undefined ? expandedRows['correct'] : expandAll;
                return (
                    <div className={`rounded-lg transition-colors duration-150 ${isCorrectOpen ? 'bg-white' : ''}`}>
                        <div
                            onClick={() => toggleRow('correct')}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isCorrectOpen ? 'hover:bg-white' : ''}`}
                        >
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="text-sm text-gray-700 flex-1">Tout correct</span>
                            <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                {correctPercent > 0 && <div className="h-full rounded-full" style={{ width: `${correctPercent}%`, background: '#1E3A8A' }} />}
                            </div>
                            <span className="text-xs font-medium text-gray-800 w-8 text-right">{correctPercent}%</span>
                            <ElevesCount count={correctEleves.length} /> <Chevron open={isCorrectOpen} />
                        </div>
                        {isCorrectOpen && <ElevesPanel eleves={correctEleves} />}
                    </div>
                );
            })()}

            {/* Erreurs — triées par fréquence */}
            {erreurs.length > 0 && (
                <div className="space-y-1.5 mt-1">
                    <p className="text-[11px] text-slate-400 px-3 mb-1">Erreurs de {label} les plus fréquentes</p>
                    {erreurs.map((err, i) => {
                        const rowKey = `err-${i}`;
                        const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                        const percent = total > 0 ? Math.round((err.eleves.length / total) * 100) : 0;
                        return (
                            <div key={i} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                                <div
                                    onClick={() => toggleRow(rowKey)}
                                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                                >
                                    <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FCEBEB' }}>
                                        <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>
                                    </div>
                                    <span className="text-sm text-gray-700 font-medium">{err.item}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 border border-red-200">{err.donnee}</span>
                                    <span className="text-gray-400 text-xs">→</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">{err.attendue}</span>
                                    <div className="flex-1" />
                                    <div className="w-16 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                        {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                    </div>
                                    <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                    <ElevesCount count={err.eleves.length} /> <Chevron open={isOpen} />
                                </div>
                                {isOpen && <ElevesPanel eleves={err.eleves} />}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Aucune erreur + 100% correct */}
            {erreurs.length === 0 && correctPercent === 100 && (
                <p className="text-sm text-emerald-600 font-medium px-3 py-1">Tous les élèves ont répondu correctement</p>
            )}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Distribution QCM ───
// ═══════════════════════════════════════

const LETTRES_QCM = 'ABCDEFGH';

// Résout une réponse (lettre ArUco, valeur en ligne, ou JSON) vers le CHOIX correspondant.
const resolveChoix = (reponse, choix = []) => {
    if (reponse == null || reponse === '') return '';
    // Lettre ArUco (A, B, …) → choix[idx]
    if (typeof reponse === 'string' && reponse.length === 1) {
        const idx = LETTRES_QCM.indexOf(reponse);
        if (idx >= 0 && idx < choix.length) return choix[idx];
    }
    // Valeur en ligne qui correspond à un choix (même sérialisation que la passation)
    const matched = choix.find(c => getChoixValue(c) === reponse);
    if (matched !== undefined) return matched;
    // JSON d'un objet élément
    if (typeof reponse === 'string' && reponse.startsWith('{')) {
        try { return JSON.parse(reponse); } catch { /* no-op */ }
    }
    return reponse;
};

// Rend une réponse QCM : élément (image/numberline/expression/geogebra) ou texte/KaTeX
// — même logique de rendu que l'élève/correction (via ChoixRenderer).
const ChoixLabel = ({ reponse, choix }) => {
    const resolved = resolveChoix(reponse, choix);
    if (resolved == null || resolved === '') return <span className="text-sm text-gray-700">(vide)</span>;
    return (
        <span className="text-sm text-gray-700 inline-flex items-center [&_svg]:max-h-[64px] [&_img]:max-h-[64px] [&_.katex]:text-inherit">
            <ChoixRenderer choix={resolved} light compact className="text-sm text-gray-700" />
        </span>
    );
};

const DistQCM = memo(({ question, repondants, expandAll }) => {
    const choix = question.choix || [];

    const groups = useMemo(() => groupByAnswer(repondants), [repondants]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    // Séparer bonne réponse et mauvaises
    const bonneReponse = groups.find(g => g.correcte);
    const mauvaises = groups.filter(g => !g.correcte);

    return (
        <div className="space-y-2 rounded-xl">
            {/* Bonne réponse en premier — fond distinct */}
            {bonneReponse && (() => {
                const percent = total > 0 ? Math.round((bonneReponse.eleves.length / total) * 100) : 0;
                const rowKey = 'correct';
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                return (
                    <div className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div
                            onClick={() => toggleRow(rowKey)}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                        >
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="flex-1 min-w-0"><ChoixLabel reponse={bonneReponse.reponse} choix={choix} /></span>
                            <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                            </div>
                            <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                            <ElevesCount count={bonneReponse.eleves.length} /> <Chevron open={isOpen} />
                        </div>
                        {isOpen && <ElevesPanel eleves={bonneReponse.eleves} />}
                    </div>
                );
            })()}

            {/* Mauvaises réponses */}
            {mauvaises.length > 0 && (
                <div className="mt-1 space-y-1.5">
                    {mauvaises.map((g, i) => {
                        const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                        const rowKey = `err-${i}`;
                        const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                        return (
                            <div key={i} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                                <div
                                    onClick={() => toggleRow(rowKey)}
                                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                                >
                                    <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FCEBEB' }}>
                                        <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>
                                    </div>
                                    <span className="flex-1 min-w-0"><ChoixLabel reponse={g.reponse} choix={choix} /></span>
                                    <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                        {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                    </div>
                                    <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                    <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                                </div>
                                {isOpen && <ElevesPanel eleves={g.eleves} />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

// ─── Distribution QCM Multi — par choix individuel (combien ont coché chaque choix) ───
const DistQCMMulti = memo(({ question, repondants, expandAll }) => {
    const choix = useMemo(() => question.choix || [], [question.choix]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    // Convertir contenu → lettre pour un choix
    const contenuToLettre = useMemo(() => {
        const map = new Map();
        choix.forEach((c, idx) => {
            const val = typeof c === 'string' ? c : JSON.stringify(c);
            map.set(val, LETTRES_QCM[idx]);
        });
        return map;
    }, [choix]);

    // Bonne combinaison en lettres triées
    const bonneCombinaisonStr = useMemo(() => {
        const br = question.bonneReponse;
        try {
            const parsed = JSON.parse(br);
            if (Array.isArray(parsed)) {
                return parsed.map(v => contenuToLettre.get(v) || '?').sort().join(', ');
            }
        } catch {}
        return '';
    }, [question.bonneReponse, contenuToLettre]);

    // Grouper par combinaison exacte — chaque réponse élève → lettres triées
    const combinaisons = useMemo(() => {
        const groups = {};
        repondants.forEach(r => {
            let lettres = '?';
            try {
                const parsed = JSON.parse(r.reponse);
                if (Array.isArray(parsed)) {
                    lettres = parsed.map(v => contenuToLettre.get(v) || '?').sort().join(', ');
                }
            } catch {
                const l = contenuToLettre.get(r.reponse);
                if (l) lettres = l;
            }
            if (!groups[lettres]) groups[lettres] = [];
            groups[lettres].push(r.nom);
        });

        // Trier : bonne combinaison en premier, puis par nombre d'élèves décroissant
        return Object.entries(groups)
            .map(([combo, eleves]) => ({ combo, eleves, isCorrect: combo === bonneCombinaisonStr }))
            .sort((a, b) => {
                if (a.isCorrect && !b.isCorrect) return -1;
                if (!a.isCorrect && b.isCorrect) return 1;
                return b.eleves.length - a.eleves.length;
            });
    }, [repondants, contenuToLettre, bonneCombinaisonStr]);

    return (
        <div className="space-y-2 rounded-xl">
            {/* Légende lettre → élément (si choix visuels : image/numberline/expression…) */}
            {choix.some(c => typeof c === 'object' && c !== null) && (
                <div className="flex flex-col gap-1.5 mb-2 p-2 rounded-lg bg-slate-50 border border-slate-200">
                    {choix.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                            <span className="font-bold w-5 flex-shrink-0">{LETTRES_QCM[i]}</span>
                            <span className="min-w-0 [&_svg]:max-h-[48px] [&_img]:max-h-[48px] [&_.katex]:text-inherit">
                                <ChoixRenderer choix={c} light compact className="text-sm text-gray-700" />
                            </span>
                        </div>
                    ))}
                </div>
            )}
            <p className="text-[11px] text-slate-400 mb-2">Plusieurs réponses possibles — combinaisons données</p>
            {/* Bonne combinaison en premier */}
            {combinaisons.map((g, i) => {
                const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                const rowKey = `combo-${i}`;
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;

                return (
                    <div key={g.combo} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div
                            onClick={() => toggleRow(rowKey)}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                        >
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: g.isCorrect ? '#EAF3DE' : '#FCEBEB' }}>
                                {g.isCorrect ? (
                                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                ) : (
                                    <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>
                                )}
                            </div>
                            <span className="text-sm text-gray-700 flex-1 font-medium">
                                {g.combo}
                            </span>
                            <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                            </div>
                            <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                            <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                        </div>
                        {isOpen && <ElevesPanel eleves={g.eleves} />}
                    </div>
                );
            })}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Distribution Texte libre ───
// ═══════════════════════════════════════

const DistTexte = memo(({ question, repondants, expandAll }) => {
    const groups = useMemo(() => groupByAnswer(repondants), [repondants]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    const bonneReponse = groups.find(g => g.correcte);
    const mauvaises = groups.filter(g => !g.correcte);

    return (
        <div className="space-y-2 rounded-xl">
            {/* Bonne réponse — fond distinct */}
            {bonneReponse && (() => {
                const percent = total > 0 ? Math.round((bonneReponse.eleves.length / total) * 100) : 0;
                const rowKey = 'correct';
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                return (
                    <div className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div
                            onClick={() => toggleRow(rowKey)}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                        >
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="text-sm text-gray-700 flex-1">"{bonneReponse.reponse || '(vide)'}"</span>
                            <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                            </div>
                            <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                            <ElevesCount count={bonneReponse.eleves.length} /> <Chevron open={isOpen} />
                        </div>
                        {isOpen && <ElevesPanel eleves={bonneReponse.eleves} />}
                    </div>
                );
            })()}

            {/* Réponses incorrectes */}
            {mauvaises.length > 0 && (
                <div className="mt-1 space-y-1.5">
                    {mauvaises.map((g, i) => {
                        const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                        const rowKey = `err-${i}`;
                        const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                        return (
                            <div key={i} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                                <div
                                    onClick={() => toggleRow(rowKey)}
                                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                                >
                                    <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FCEBEB' }}>
                                        <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>
                                    </div>
                                    <span className="text-sm text-gray-700 flex-1">"{g.reponse || '(vide)'}"</span>
                                    <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                        {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                    </div>
                                    <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                    <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                                </div>
                                {isOpen && <ElevesPanel eleves={g.eleves} />}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
});

// ══��══���═════════════════════════════════
// ─── Distribution Relier ───
// ═══════════════════════════════════════

const DistRelier = memo(({ question, repondants, expandAll }) => {
    const bonnesPaires = useMemo(() => parsePaires(question.bonneReponse), [question.bonneReponse]);
    const total = repondants.length;

    // Calcul des erreurs de connexion pour DistErreurs
    const { correctEleves, erreurs } = useMemo(() => {
        if (!bonnesPaires.length || !total) return { correctEleves: [], erreurs: [] };

        // Map source → cible correcte
        const correctMap = new Map(bonnesPaires.map(([s, t]) => [s, t]));
        const errMap = {};
        const correctSet = new Set(repondants.map(r => r.nom));

        repondants.forEach(r => {
            const paires = parsePaires(r.reponse);
            correctMap.forEach((bonTarget, source) => {
                const match = paires.find(p => p[0] === source);
                const donnee = match ? match[1] : '(non relié)';
                if (donnee !== bonTarget) {
                    correctSet.delete(r.nom);
                    const key = `${source}|${donnee}`;
                    if (!errMap[key]) errMap[key] = { item: source, donnee, attendue: bonTarget, eleves: [] };
                    errMap[key].eleves.push(r.nom);
                }
            });
        });

        return {
            correctEleves: [...correctSet],
            erreurs: Object.values(errMap).sort((a, b) => b.eleves.length - a.eleves.length),
        };
    }, [bonnesPaires, repondants, total]);

    return <DistErreurs label="connexion" correctEleves={correctEleves} total={total} erreurs={erreurs} expandAll={expandAll} />;
});

// ═══════════════════════════════════════
// ─── Distribution Ordonner ───
// ═══════════════════════════════════════

const DistOrdonner = memo(({ repondants, expandAll }) => {
    const total = repondants.length;

    // Calcul des erreurs d'ordre pour DistErreurs
    // Pour ordonner, on compare item par item à chaque position
    const { correctEleves, erreurs } = useMemo(() => {
        if (!total) return { correctEleves: [], erreurs: [] };

        // Trouver le bon ordre depuis les répondants corrects
        const bonRepondant = repondants.find(r => r.correcte);
        if (!bonRepondant) return { correctEleves: [], erreurs: [] };

        let bonOrdre;
        try { bonOrdre = JSON.parse(bonRepondant.reponse); } catch { return { correctEleves: [], erreurs: [] }; }
        if (!Array.isArray(bonOrdre)) return { correctEleves: [], erreurs: [] };

        const errMap = {};
        const correctSet = new Set();

        repondants.forEach(r => {
            if (r.correcte) {
                correctSet.add(r.nom);
                return;
            }
            let eleveOrdre;
            try { eleveOrdre = JSON.parse(r.reponse); } catch { return; }
            if (!Array.isArray(eleveOrdre)) return;

            // Comparer chaque position
            eleveOrdre.forEach((item, pos) => {
                if (bonOrdre[pos] !== item) {
                    const bonnePos = bonOrdre.indexOf(item);
                    const key = `${item}|${pos}`;
                    if (!errMap[key]) errMap[key] = {
                        item,
                        donnee: `pos. ${pos + 1}`,
                        attendue: `pos. ${bonnePos + 1}`,
                        eleves: []
                    };
                    errMap[key].eleves.push(r.nom);
                }
            });
        });

        return {
            correctEleves: [...correctSet],
            erreurs: Object.values(errMap).sort((a, b) => b.eleves.length - a.eleves.length),
        };
    }, [repondants, total]);

    return <DistErreurs label="ordre" correctEleves={correctEleves} total={total} erreurs={erreurs} expandAll={expandAll} />;
});

// ═══════════════════════════════════════
// ─── Distribution Texte à trous ───
// ═══════════════════════════════════════

const DistTexteTrou = memo(({ question, repondants, expandAll }) => {
    const bonsTrous = useMemo(() => extractTrous(question.bonneReponse), [question.bonneReponse]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    // Pour chaque trou : bonne réponse + variantes incorrectes groupées
    const trousStats = useMemo(() => {
        if (!bonsTrous.length || !total) return [];

        return bonsTrous.map((mot, idx) => {
            const correctEleves = [];
            const erreurs = new Map();

            repondants.forEach(r => {
                const eleveTrous = extractTrous(r.reponse);
                const repondu = eleveTrous[idx] || '(vide)';
                if (repondu === mot) {
                    correctEleves.push(r.nom);
                } else {
                    if (!erreurs.has(repondu)) erreurs.set(repondu, []);
                    erreurs.get(repondu).push(r.nom);
                }
            });

            return {
                mot, index: idx + 1,
                correctEleves,
                percent: total > 0 ? Math.round((correctEleves.length / total) * 100) : 0,
                erreurs: [...erreurs.entries()].map(([m, noms]) => ({
                    mot: m, eleves: noms, percent: total > 0 ? Math.round((noms.length / total) * 100) : 0
                })).sort((a, b) => b.eleves.length - a.eleves.length),
            };
        });
    }, [bonsTrous, repondants, total]);

    // Phrase complète avec trous en pills teal
    const phraseDisplay = useMemo(() => {
        if (!question.contenu?.phrase) return null;
        const parts = question.contenu.phrase.split(/(\{\{[^}]+\}\})/g);
        return parts.map((part, i) => {
            const match = part.match(/^\{\{(.+)\}\}$/);
            if (match) return <span key={i} className="inline-block mx-0.5 bg-teal-50 text-teal-800 font-medium px-2.5 py-0.5 rounded-md border-b-2 border-teal-500 text-sm">{match[1]}</span>;
            return <span key={i}>{part}</span>;
        });
    }, [question.contenu?.phrase]);

    // Élèves qui ont tout correct (tous les trous justes)
    const correctEleves = useMemo(() => {
        if (!trousStats.length) return [];
        return repondants
            .filter(r => {
                const eleveTrous = extractTrous(r.reponse);
                return bonsTrous.every((mot, idx) => eleveTrous[idx] === mot);
            })
            .map(r => r.nom);
    }, [repondants, bonsTrous, trousStats]);

    return (
        <div className="space-y-3 rounded-xl">
            {/* Phrase complète avec trous en pills teal */}
            {phraseDisplay && (
                <div className="px-3 py-2.5 text-sm text-gray-700 leading-8">
                    {phraseDisplay}
                </div>
            )}

            {/* Tout correct — fond blanc quand ouvert */}
            {(() => {
                const isOpen = expandedRows['all-correct'] !== undefined ? expandedRows['all-correct'] : expandAll;
                return (
                    <div className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div
                            onClick={() => toggleRow('all-correct')}
                            className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}
                        >
                            <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="text-sm text-gray-700 flex-1">Tout correct</span>
                            <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                {total > 0 && <div className="h-full rounded-full" style={{ width: `${Math.round((correctEleves.length / total) * 100)}%`, background: '#1E3A8A' }} />}
                            </div>
                            <span className="text-xs font-medium text-gray-800 w-8 text-right">{total > 0 ? Math.round((correctEleves.length / total) * 100) : 0}%</span>
                            <ElevesCount count={correctEleves.length} /> <Chevron open={isOpen} />
                        </div>
                        {isOpen && <ElevesPanel eleves={correctEleves} />}
                    </div>
                );
            })()}

            {/* Par trou — compact si 100%, détaillé sinon */}
            {trousStats.map((t, ti) => {
                // 100% correct → ligne compacte
                if (t.percent === 100) {
                    return (
                        <div key={ti} className="flex items-center gap-2 px-3 py-1">
                            <div className="w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                <svg width="7" height="7" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </div>
                            <span className="text-xs text-slate-400">Trou {t.index}</span>
                            <span className="bg-teal-50 text-teal-800 font-medium px-2 py-0.5 rounded-md border-b-2 border-teal-500 text-xs">{t.mot}</span>
                            <span className="text-xs text-emerald-600 font-medium">100%</span>
                        </div>
                    );
                }

                // Avec erreurs → détaillé
                return (
                    <div key={ti} className="space-y-1.5">
                        <p className="text-[12px] font-medium text-gray-600 px-3 mb-1">
                            Trou {t.index} : <span className="bg-teal-50 text-teal-800 font-medium px-2 py-0.5 rounded-md border-b-2 border-teal-500 text-xs">{t.mot}</span>
                        </p>

                        {/* Bonne réponse */}
                        {(() => {
                            const rowKey = `${ti}-correct`;
                            const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                            return (
                                <div className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                                    <div onClick={() => toggleRow(rowKey)} className={`flex items-center gap-2.5 px-3 py-1.5 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                                        <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#EAF3DE' }}>
                                            <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        </div>
                                        <span className="text-sm text-gray-700 flex-1">{t.mot}</span>
                                        <div className="w-16 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                            {t.percent > 0 && <div className="h-full rounded-full" style={{ width: `${t.percent}%`, background: '#1E3A8A' }} />}
                                        </div>
                                        <span className="text-xs font-medium text-gray-800 w-8 text-right">{t.percent}%</span>
                                        <ElevesCount count={t.correctEleves.length} /> <Chevron open={isOpen} />
                                    </div>
                                    {isOpen && <ElevesPanel eleves={t.correctEleves} />}
                                </div>
                            );
                        })()}

                        {/* Erreurs */}
                        {t.erreurs.map((err, ei) => {
                            const rowKey = `${ti}-err-${ei}`;
                            const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                            return (
                                <div key={ei} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                                    <div onClick={() => toggleRow(rowKey)} className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors duration-150 rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                                        <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#FCEBEB' }}>
                                            <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>
                                        </div>
                                        <span className="text-sm text-gray-700 flex-1">"{err.mot}"</span>
                                        <div className="w-16 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                            {err.percent > 0 && <div className="h-full rounded-full" style={{ width: `${err.percent}%`, background: '#1E3A8A' }} />}
                                        </div>
                                        <span className="text-xs font-medium text-gray-800 w-8 text-right">{err.percent}%</span>
                                        <ElevesCount count={err.eleves.length} /> <Chevron open={isOpen} />
                                    </div>
                                    {isOpen && <ElevesPanel eleves={err.eleves} />}
                                </div>
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Distribution Classer ───
// ═══════════════════════════════════════

const DistClasser = memo(({ question, repondants, expandAll }) => {
    const bonClassement = useMemo(() => parseClassement(question.bonneReponse), [question.bonneReponse]);
    const total = repondants.length;

    // Calcul des erreurs de placement pour DistErreurs
    const { correctEleves, erreurs } = useMemo(() => {
        if (!Object.keys(bonClassement).length || !total) return { correctEleves: [], erreurs: [] };

        // Map : item → catégorie correcte
        const correctMap = {};
        Object.entries(bonClassement).forEach(([cat, items]) => {
            (items || []).forEach(item => { correctMap[item] = cat; });
        });

        const errMap = {};
        const correctSet = new Set(repondants.map(r => r.nom)); // tous corrects par défaut

        repondants.forEach(r => {
            const classement = parseClassement(r.reponse);
            Object.entries(classement).forEach(([catDonnee, items]) => {
                (items || []).forEach(item => {
                    if (correctMap[item] !== catDonnee) {
                        correctSet.delete(r.nom); // cet élève a au moins une erreur
                        const key = `${item}|${catDonnee}`;
                        if (!errMap[key]) errMap[key] = { item, donnee: catDonnee, attendue: correctMap[item] || '?', eleves: [] };
                        errMap[key].eleves.push(r.nom);
                    }
                });
            });
        });

        return {
            correctEleves: [...correctSet],
            erreurs: Object.values(errMap).sort((a, b) => b.eleves.length - a.eleves.length),
        };
    }, [bonClassement, repondants, total]);

    return <DistErreurs label="placement" correctEleves={correctEleves} total={total} erreurs={erreurs} expandAll={expandAll} />;
});

// ═══════════════════════════════════════
// ─── Distribution Tableau ([ligne|colonne]) ───
// ═══════════════════════════════════════

const DistTableau = memo(({ question, repondants, expandAll }) => {
    const total = repondants.length;
    const bonnesPairs = useMemo(() => parseTableReponse(question.bonneReponse), [question.bonneReponse]);

    // Calcul des erreurs par ligne pour DistErreurs
    const { correctEleves, erreurs } = useMemo(() => {
        if (!bonnesPairs.length || !total) return { correctEleves: [], erreurs: [] };

        // Map ligne → colonne correcte
        const correctMap = new Map(bonnesPairs.map(p => [p.ligne, p.colonne]));
        const errMap = {};
        const correctSet = new Set(repondants.map(r => r.nom));

        repondants.forEach(r => {
            const elevePairs = parseTableReponse(r.reponse);
            correctMap.forEach((bonneColonne, ligne) => {
                const match = elevePairs.find(p => p.ligne === ligne);
                const donnee = match?.colonne || '(non coché)';
                if (donnee !== bonneColonne) {
                    correctSet.delete(r.nom);
                    const key = `${ligne}|${donnee}`;
                    if (!errMap[key]) errMap[key] = { item: ligne, donnee, attendue: bonneColonne, eleves: [] };
                    errMap[key].eleves.push(r.nom);
                }
            });
        });

        return {
            correctEleves: [...correctSet],
            erreurs: Object.values(errMap).sort((a, b) => b.eleves.length - a.eleves.length),
        };
    }, [bonnesPairs, repondants, total]);

    return <DistErreurs label="ligne" correctEleves={correctEleves} total={total} erreurs={erreurs} expandAll={expandAll} />;
});

// ═══════════════════════════════════════
// ─── Distribution Placer des points ───
// Une mini droite graduée par réponse distincte (points colorés vert/rouge).
// ═══════════════════════════════════════

const DistPlacerPoints = memo(({ question, repondants, expandAll }) => {
    const numberline = question.contenu?.numberline || null;
    const groups = useMemo(() => groupByAnswer(repondants), [repondants]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    const correctPoints = useMemo(() => {
        try { return JSON.parse(question.bonneReponse || '{}'); } catch { return {}; }
    }, [question.bonneReponse]);

    // Construit la config droite graduée pour une réponse (points colorés selon exactitude)
    const buildConfig = (reponse) => {
        if (!numberline) return null;
        let pts;
        try { pts = JSON.parse(reponse || '{}'); } catch { return null; }
        const points = Object.entries(pts).map(([label, v]) => {
            const value = parseFloat(String(v).replace(',', '.'));
            if (Number.isNaN(value)) return null;
            const attendu = correctPoints[label];
            const juste = attendu !== undefined
                && Math.abs(value - parseFloat(String(attendu).replace(',', '.'))) < 0.01;
            return { value, label, showValue: true, color: juste ? '#22c55e' : '#ef4444' };
        }).filter(Boolean);
        return { ...numberline, points, showArrow: true, showStart: true, showEnd: true };
    };

    return (
        <div className="space-y-2 rounded-xl">
            {groups.map((g, i) => {
                const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                const rowKey = `pp-${i}`;
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                const config = buildConfig(g.reponse);
                return (
                    <div key={rowKey} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div onClick={() => toggleRow(rowKey)} className={`px-3 py-2 cursor-pointer rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                            <div className="flex items-center gap-2.5">
                                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: g.correcte ? '#EAF3DE' : '#FCEBEB' }}>
                                    {g.correcte
                                        ? <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        : <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>}
                                </div>
                                <div className="flex-1" />
                                <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                    {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                </div>
                                <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                            </div>
                            <div className="mt-1 [&_svg]:max-h-[90px]">
                                {config && config.points.length > 0
                                    ? <NumberLineElementEleve config={config} theme="light" />
                                    : <span className="text-xs text-slate-400 italic">Réponse vide / illisible</span>}
                            </div>
                        </div>
                        {isOpen && <ElevesPanel eleves={g.eleves} />}
                    </div>
                );
            })}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Distribution Programme de construction ───
// Une mini figure par réponse distincte (shapes de l'élève via le renderer).
// ═══════════════════════════════════════

const DistConstruction = memo(({ question, repondants, expandAll }) => {
    const c = question.contenu || {};
    const groups = useMemo(() => groupByAnswer(repondants), [repondants]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    // La réponse élève d'un angle_ray = { type:'ray', angleDeg } SANS `from` (le sommet).
    // On réinjecte `from`/`labelEnd` depuis la bonne réponse (qui les contient) pour que
    // le renderer puisse tracer le rayon depuis le bon sommet.
    const correctShapes = useMemo(() => {
        try { return JSON.parse(question.bonneReponse || '{}'); } catch { return {}; }
    }, [question.bonneReponse]);
    const patchFrom = (reponse) => {
        let s;
        try { s = JSON.parse(reponse || '{}'); } catch { return reponse; }
        let changed = false;
        Object.keys(s).forEach((id) => {
            const sh = s[id];
            const cor = correctShapes[id];
            if (sh && sh.type === 'ray' && sh.from == null && cor) {
                s[id] = { ...sh, from: cor.from, labelEnd: sh.labelEnd ?? cor.labelEnd };
                changed = true;
            }
        });
        return changed ? JSON.stringify(s) : reponse;
    };

    return (
        <div className="space-y-2 rounded-xl">
            {groups.map((g, i) => {
                const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                const rowKey = `pc-${i}`;
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                return (
                    <div key={rowKey} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div onClick={() => toggleRow(rowKey)} className={`px-3 py-2 cursor-pointer rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                            <div className="flex items-center gap-2.5">
                                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: g.correcte ? '#EAF3DE' : '#FCEBEB' }}>
                                    {g.correcte
                                        ? <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        : <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>}
                                </div>
                                <div className="flex-1" />
                                <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                    {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                </div>
                                <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                            </div>
                            <div className="mt-1 [&_svg]:max-h-[160px]">
                                {c.viewBox
                                    ? <ProgrammeConstructionRenderer
                                        viewBox={c.viewBox}
                                        points={c.points || []}
                                        preShapes={c.preShapes || []}
                                        tasks={c.tasks || []}
                                        pxPerCm={c.pxPerCm}
                                        bonneReponse={patchFrom(g.reponse)}
                                        showConsignes={false}
                                        light
                                      />
                                    : <span className="text-xs text-slate-400 italic">Figure indisponible</span>}
                            </div>
                        </div>
                        {isOpen && <ElevesPanel eleves={g.eleves} />}
                    </div>
                );
            })}
        </div>
    );
});


// ═══════════════════════════════════════
// ─── Distribution Placer les aiguilles ───
// Regroupement par SIGNATURE DE VERDICTS (petite / grande / trotteuse justes
// ou non via questionUtils.aiguilleJuste) : les angles bruts sont quasi tous
// distincts, le verdict par aiguille est la vraie information du prof.
// ═══════════════════════════════════════

const NOMS_AIG_DIST = { heures: 'petite', minutes: 'grande', secondes: 'trotteuse' };

const DistAiguilles = memo(({ question, repondants, expandAll }) => {
    const cible = useMemo(() => {
        try { return JSON.parse(question.bonneReponse || '{}').aiguilles || null; } catch { return null; }
    }, [question.bonneReponse]);
    const mains = useMemo(
        () => ['heures', 'minutes', ...(cible && cible.sec != null ? ['secondes'] : [])],
        [cible],
    );
    const groups = useMemo(() => {
        const map = new Map();
        repondants.forEach((r) => {
            let angles = null;
            try { angles = JSON.parse(r.reponse || '{}'); } catch { /* reponse vide */ }
            const verdicts = mains.map((m) => (cible ? aiguilleJuste(m, angles ? angles[m] : null, cible) : false));
            const key = verdicts.map((v) => (v ? '1' : '0')).join('');
            if (!map.has(key)) map.set(key, { verdicts, correcte: verdicts.every(Boolean), reponse: r.reponse, eleves: [] });
            map.get(key).eleves.push(r.nom);
        });
        return [...map.values()].sort((a, b) => {
            if (a.correcte !== b.correcte) return a.correcte ? -1 : 1;
            return b.eleves.length - a.eleves.length;
        });
    }, [repondants, cible, mains]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    return (
        <div className="space-y-2 rounded-xl">
            {groups.map((g, i) => {
                const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                const rowKey = `aig-${i}`;
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                return (
                    <div key={rowKey} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div onClick={() => toggleRow(rowKey)} className={`px-3 py-2 cursor-pointer rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                            <div className="flex items-center gap-2.5">
                                <div className="flex items-center gap-1 flex-wrap">
                                    {mains.map((m, j) => (
                                        <span key={m} className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium ${g.verdicts[j] ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                            {NOMS_AIG_DIST[m]} {g.verdicts[j] ? '✓' : '✗'}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex-1" />
                                <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                    {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                </div>
                                <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                            </div>
                            <div className="mt-1 [&_svg]:max-h-[130px]">
                                <HorlogeAiguillesRenderer reponse={g.reponse} avecTrotteuse={!!(cible && cible.sec != null)} light />
                            </div>
                        </div>
                        {isOpen && <ElevesPanel eleves={g.eleves} />}
                    </div>
                );
            })}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Distribution Placer des points dans un repère ───
// Une ligne « Juste », puis une ligne par réponse fausse distincte, la plus
// fréquente d'abord : les mêmes points, quelle que soit l'écriture (« 1;-4 »,
// « 1,0;-4 »), font la même ligne, les coordonnées se comparent en nombres.
// La ligne dit en clair les points mal placés ; le clic ouvre le petit repère
// de cette réponse et les noms. Pas de figure pour la ligne juste : c'est le
// corrigé (épreuve de Musk, 2026-10-08).
// ═══════════════════════════════════════

const lireXY = (v) => {
    const parts = String(v ?? '').replace(/−/g, '-').split(';');
    if (parts.length !== 2) return null;
    const x = parseFloat(parts[0].replace(',', '.'));
    const y = parseFloat(parts[1].replace(',', '.'));
    return Number.isNaN(x) || Number.isNaN(y) ? null : { x, y };
};
const memeXY = (a, b) => Boolean(a && b) && Math.abs(a.x - b.x) < 0.001 && Math.abs(a.y - b.y) < 0.001;
const nombreFr = (n) => String(Math.round(n * 1000) / 1000).replace('.', ',').replace('-', '−');
const coordonnees = (p) => `(${nombreFr(p.x)} ; ${nombreFr(p.y)})`;
const NB_POINTS_DITS = 2;

/** Les points mal placés d'une réponse, en clair : « H en (−4 ; 1) au lieu de (1 ; −4) ». */
const pointsMalPlaces = (lus, attendus) => {
    const faux = Object.entries(attendus)
        .map(([label, v]) => ({ label, attendu: lireXY(v), donne: lus ? lus[label] : null }))
        .filter((p) => !memeXY(p.donne, p.attendu));
    if (!faux.length) return 'Réponse fausse';
    const dits = faux.slice(0, NB_POINTS_DITS).map((p) => {
        if (!p.donne) return `${p.label} non placé`;
        return p.attendu ? `${p.label} en ${coordonnees(p.donne)} au lieu de ${coordonnees(p.attendu)}` : `${p.label} en ${coordonnees(p.donne)}`;
    });
    const reste = faux.length - NB_POINTS_DITS;
    return [...dits, ...(reste > 0 ? [`et ${reste} ${reste > 1 ? 'autres points' : 'autre point'}`] : [])].join(' · ');
};

const DistPlacerPoints2D = memo(({ question, repondants, expandAll }) => {
    const contenu = question.contenu || {};
    const attendus = useMemo(() => {
        try { return JSON.parse(question.bonneReponse || '{}'); } catch { return {}; }
    }, [question.bonneReponse]);
    const groupes = useMemo(() => {
        const map = new Map();
        repondants.forEach((r) => {
            let brut = null;
            try { brut = r.reponse ? JSON.parse(r.reponse) : null; } catch { brut = null; }
            const lus = brut && typeof brut === 'object' && !Array.isArray(brut)
                ? Object.fromEntries(Object.entries(brut).map(([label, v]) => [label, lireXY(v)]))
                : null;
            const vide = !lus || !Object.values(lus).some(Boolean);
            // Le verdict est celui de la copie (le score) ; une réponse fausse se range par ses points lus en nombres.
            const juste = Boolean(r.correcte);
            const cle = juste ? 'juste' : vide ? 'vide'
                : Object.keys(lus).sort().map((label) => `${label}:${lus[label] ? `${lus[label].x};${lus[label].y}` : '?'}`).join('|');
            if (!map.has(cle)) map.set(cle, { cle, juste, vide, lus, eleves: [] });
            map.get(cle).eleves.push(r.nom);
        });
        return [...map.values()].sort((a, b) => {
            if (a.juste !== b.juste) return a.juste ? -1 : 1;
            return b.eleves.length - a.eleves.length;
        });
    }, [repondants]);
    const total = repondants.length;
    const [expandedRows, setExpandedRows] = useState({});
    const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
    useEffect(() => { if (!expandAll) setExpandedRows({}); }, [expandAll]);

    /** Les points de l'élève sur le repère : verts s'ils sont à leur place, rouges sinon. */
    const pointsDe = (lus) => Object.entries(lus || {})
        .filter(([, xy]) => xy)
        .map(([label, xy]) => ({ ...xy, label, color: memeXY(xy, lireXY(attendus[label])) ? '#22c55e' : '#ef4444' }));

    return (
        <div className="space-y-2 rounded-xl">
            {groupes.map((g) => {
                const percent = total > 0 ? Math.round((g.eleves.length / total) * 100) : 0;
                const rowKey = `p2d-${g.cle}`;
                const isOpen = expandedRows[rowKey] !== undefined ? expandedRows[rowKey] : expandAll;
                const libelle = g.juste ? 'Juste' : g.vide ? 'Réponse vide' : pointsMalPlaces(g.lus, attendus);
                const avecFigure = !g.juste && !g.vide && contenu.repere;
                return (
                    <div key={rowKey} className={`rounded-lg transition-colors duration-150 ${isOpen ? 'bg-white' : ''}`}>
                        <div onClick={() => toggleRow(rowKey)} className={`px-3 py-2 cursor-pointer rounded-lg ${!isOpen ? 'hover:bg-white' : ''}`}>
                            <div className="flex items-center gap-2.5">
                                <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: g.juste ? '#EAF3DE' : '#FCEBEB' }}>
                                    {g.juste
                                        ? <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M2.5 6L5 8.5L9.5 3.5" stroke="#3B6D11" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                        : <svg width="8" height="8" viewBox="0 0 12 12" fill="none"><path d="M3 3L9 9M9 3L3 9" stroke="#A32D2D" strokeWidth="2" strokeLinecap="round"/></svg>}
                                </div>
                                <span className="min-w-0 flex-1 text-xs font-medium text-gray-800">{libelle}</span>
                                <div className="w-20 h-1 rounded-full bg-slate-300/50 overflow-hidden flex-shrink-0">
                                    {percent > 0 && <div className="h-full rounded-full" style={{ width: `${percent}%`, background: '#1E3A8A' }} />}
                                </div>
                                <span className="text-xs font-medium text-gray-800 w-8 text-right">{percent}%</span>
                                <ElevesCount count={g.eleves.length} /> <Chevron open={isOpen} />
                            </div>
                        </div>
                        {isOpen && avecFigure ? (
                            <div className="px-3 pb-1 [&_svg]:max-h-[160px]">
                                <Repere2DRenderer repere={contenu.repere} points={pointsDe(g.lus)} dejaPlaces={contenu.dejaPlaces || []} decor={contenu.decor || null} relier={contenu.relier || null} light titre={null} />
                            </div>
                        ) : null}
                        {isOpen && <ElevesPanel eleves={g.eleves} />}
                    </div>
                );
            })}
        </div>
    );
});

// ═══════════════════════════════════════
// ─── Composant principal — Routing par type ───
// ═══════════════════════════════════════

const DistributionExerciseur = memo(({ question, repondants, expandAll = false }) => {
    if (!repondants?.length) {
        return <p className="text-xs text-slate-400 italic py-2">Aucune réponse pour cette question.</p>;
    }

    const { type, subType } = question;

    if (type === 'qcm' || type === 'qcm_multiple') {
        // QCM Multi → distribution par combinaisons
        if (question.multi || question.contenu?.multi || type === 'qcm_multiple') {
            return <DistQCMMulti question={question} repondants={repondants} expandAll={expandAll} />;
        }
        // Tableau → distribution par ligne/colonne
        const tableConfig = getTableElementConfig({ bonne_reponse: question.bonneReponse, elements_additionnels: question.elements_additionnels });
        if (tableConfig) return <DistTableau question={question} repondants={repondants} expandAll={expandAll} />;
        // QCM simple
        return <DistQCM question={question} repondants={repondants} expandAll={expandAll} />;
    }
    if (type === 'texte') return <DistTexte question={question} repondants={repondants} expandAll={expandAll} />;

    if (type === 'association') {
        if (subType === 'relier') return <DistRelier question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'ordonner') return <DistOrdonner repondants={repondants} expandAll={expandAll} />;
        if (subType === 'texte_trou') return <DistTexteTrou question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'classer') return <DistClasser question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'placer_points') return <DistPlacerPoints question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'placer_points_2d') return <DistPlacerPoints2D question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'programme_construction') return <DistConstruction question={question} repondants={repondants} expandAll={expandAll} />;
        if (subType === 'placer_aiguilles') return <DistAiguilles question={question} repondants={repondants} expandAll={expandAll} />;
    }

    // Fallback
    return <DistQCM question={question} repondants={repondants} expandAll={expandAll} />;
});

export default DistributionExerciseur;
